import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  LayoutGrid, List, AlertTriangle, Clock, CheckCircle, ChevronRight,
  User, Calendar, MapPin, DollarSign, Filter, Search, CircleDot, Monitor, Smartphone, Camera, Image, Trash2, Pencil, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  in_progress: { bg: 'bg-sky-100', text: 'text-sky-700', dot: 'bg-sky-500' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  blocked: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
};

function DaysChip({ days, isOverdue }) {
  if (days === 0 && !isOverdue) return null;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold font-mono ${
      isOverdue ? 'bg-red-100 text-red-700' : days > 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
    }`}>
      {isOverdue && <AlertTriangle className="w-2.5 h-2.5" />}
      {days}d
    </span>
  );
}

function ProjectCard({ project, onClick }) {
  const pct = project.progress_pct || 0;
  return (
    <Card
      className={`kanban-card border cursor-pointer transition-all hover:shadow-lg active:scale-[0.99] ${
        project.is_overdue ? 'border-red-200 bg-red-50/30' : 'border-slate-100 bg-white'
      }`}
      onClick={onClick}
      data-testid={`job-card-${project.id}`}
    >
      <CardContent className="p-3.5">
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-800 truncate">{project.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {project.customer_city && (
                <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                  <MapPin className="w-2.5 h-2.5" />{project.customer_city}
                </span>
              )}
              {project.pool_type && (
                <span className="text-[10px] text-slate-400 capitalize">{project.pool_type}</span>
              )}
            </div>
          </div>
          <DaysChip days={project.days_in_stage} isOverdue={project.is_overdue} />
        </div>
        <Progress value={pct} className="h-1.5 mb-2" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {project.current_assignee ? (
              <div className="flex items-center gap-1">
                <div className="w-5 h-5 rounded-full bg-sky-100 flex items-center justify-center">
                  <span className="text-[9px] font-bold text-sky-700">{project.current_assignee.charAt(0)}</span>
                </div>
                <span className="text-[10px] text-slate-500 truncate max-w-[80px]">{project.current_assignee}</span>
              </div>
            ) : (
              <span className="text-[10px] text-slate-300 italic">Unassigned</span>
            )}
          </div>
          {project.bid_total > 0 && (
            <span className="text-[10px] font-mono font-semibold text-slate-500">
              ${(project.bid_total / 1000).toFixed(0)}k
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* Mobile-optimized job card for list view */
function MobileJobCard({ project, onClick }) {
  const pct = project.progress_pct || 0;
  const sc = STATUS_COLORS[project.current_stage === 'Completed' ? 'completed' : 'in_progress'];
  return (
    <Card
      className={`border cursor-pointer active:scale-[0.99] transition-all ${
        project.is_overdue ? 'border-red-200 bg-red-50/30' : 'border-slate-100'
      }`}
      onClick={onClick}
      data-testid={`mobile-job-${project.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="min-w-0 flex-1">
            <p className="text-base font-bold text-slate-800 truncate">{project.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {project.customer_city && <span className="text-xs text-slate-400">{project.customer_city}</span>}
              <span className="text-xs text-slate-300">|</span>
              <span className="text-xs text-slate-400 capitalize">{project.pool_type}</span>
            </div>
          </div>
          {project.is_overdue && <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />}
        </div>
        <div className="flex items-center gap-2 mb-3">
          <Badge className="bg-sky-100 text-sky-700 text-xs font-semibold">{project.current_stage}</Badge>
          <DaysChip days={project.days_in_stage} isOverdue={project.is_overdue} />
          {project.bid_total > 0 && (
            <span className="ml-auto text-xs font-mono font-semibold text-slate-600">
              ${project.bid_total.toLocaleString()}
            </span>
          )}
        </div>
        <Progress value={pct} className="h-2 mb-2 rounded-full" />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {project.current_assignee ? (
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-sky-700">{project.current_assignee.charAt(0)}</span>
                </div>
                <span className="text-xs text-slate-600">{project.current_assignee}</span>
              </div>
            ) : (
              <span className="text-xs text-slate-300 italic">Unassigned</span>
            )}
          </div>
          <span className="text-xs text-slate-400">{pct}%</span>
        </div>
      </CardContent>
    </Card>
  );
}

export default function JobsBoard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('kanban');
  const [forceDesktop, setForceDesktop] = useState(false);
  const [board, setBoard] = useState({});
  const [stages, setStages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [stageDialog, setStageDialog] = useState(null);
  const [search, setSearch] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [stagePhotos, setStagePhotos] = useState({});
  const [detailTab, setDetailTab] = useState('stages');
  const [expenses, setExpenses] = useState([]);
  const [expenseDialog, setExpenseDialog] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [expForm, setExpForm] = useState({ vendor_name: '', amount: 0, category: '', date: '', notes: '' });
  const [photoUploadStage, setPhotoUploadStage] = useState(null);
  const [photoCategory, setPhotoCategory] = useState('');
  const [changeOrders, setChangeOrders] = useState([]);
  const [jobCosting, setJobCosting] = useState(null);
  const [coDialog, setCoDialog] = useState(false);
  const [coForm, setCoForm] = useState({ title: '', reason_code: 'scope_change', scope_description: '', cost_impact: 0, price_impact: 0, days_added: 0, notes: '' });

  const loadBoard = useCallback(async () => {
    try {
      const r = await api.get('/projects/board');
      setBoard(r.data.board || {});
      setStages(r.data.stages || []);
      setTeamMembers(r.data.users || []);
    } catch {}
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const r = await api.get('/projects');
      setProjects(r.data);
    } catch {}
  }, []);

  useEffect(() => { loadBoard(); loadProjects(); }, [loadBoard, loadProjects]);

  const handleStageUpdate = async (projectId, stageIdx, updates) => {
    try {
      await api.put(`/projects/${projectId}/stages/${stageIdx}`, updates);
      toast.success('Stage updated');
      loadBoard();
      loadProjects();
      if (selectedProject?.id === projectId) {
        const r = await api.get(`/projects/${projectId}`);
        setSelectedProject(r.data);
      }
    } catch { toast.error('Update failed'); }
  };

  const openProjectDetail = async (projectId) => {
    const r = await api.get(`/projects/${projectId}`);
    setSelectedProject(r.data);
    setDetailTab('stages');
    try {
      const photos = await api.get(`/projects/${projectId}/all-photos`);
      const grouped = {};
      for (const p of photos.data) { grouped[p.stage_idx] = grouped[p.stage_idx] || []; grouped[p.stage_idx].push(p); }
      setStagePhotos(grouped);
    } catch { setStagePhotos({}); }
    try { const e = await api.get(`/projects/${projectId}/expenses`); setExpenses(e.data); } catch { setExpenses([]); }
    try { const co = await api.get(`/projects/${projectId}/change-orders`); setChangeOrders(co.data); } catch { setChangeOrders([]); }
    try { const jc = await api.get(`/projects/${projectId}/job-costing`); setJobCosting(jc.data); } catch { setJobCosting(null); }
  };

  const handlePhotoUpload = async (projectId, stageIdx, file, category) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/projects/${projectId}/stages/${stageIdx}/photos`, fd);
      toast.success(`Photo uploaded${category ? ': ' + category : ''}`);
      openProjectDetail(projectId);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
  };

  const handleSaveExpense = async () => {
    try {
      if (editExpense) {
        await api.put(`/projects/${selectedProject.id}/expenses/${editExpense.id}`, expForm);
        toast.success('Expense updated');
      } else {
        await api.post(`/projects/${selectedProject.id}/expenses`, expForm);
        toast.success('Expense added');
      }
      setExpenseDialog(false); setEditExpense(null); setExpForm({ vendor_name: '', amount: 0, category: '', date: '', notes: '' });
      const e = await api.get(`/projects/${selectedProject.id}/expenses`); setExpenses(e.data);
    } catch { toast.error('Failed'); }
  };

  const handleDeleteExpense = async (expId) => {
    if (!window.confirm('Delete this expense?')) return;
    await api.delete(`/projects/${selectedProject.id}/expenses/${expId}`);
    toast.success('Deleted');
    const e = await api.get(`/projects/${selectedProject.id}/expenses`); setExpenses(e.data);
  };

  const handleDeletePhoto = async (projectId, photoId) => {
    await api.delete(`/projects/${projectId}/photos/${photoId}`);
    toast.success('Photo deleted');
    openProjectDetail(projectId);
  };

  const filteredProjects = projects.filter(p => {
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterAssignee && filterAssignee !== 'all') {
      const hasAssignee = p.stages?.some(s => s.assigned_name === filterAssignee || s.assigned_to === filterAssignee);
      if (!hasAssignee && p.assigned_to !== filterAssignee) return false;
    }
    return true;
  });

  const allAssignees = [...new Set(
    projects.flatMap(p => (p.stages || []).map(s => s.assigned_name).filter(Boolean))
  )];

  // ─── PROJECT DETAIL VIEW ───
  if (selectedProject) {
    const sortedStages = (selectedProject.stages || []).sort((a, b) => (a.order || 0) - (b.order || 0));
    const currentIdx = sortedStages.findIndex(s => s.status === 'in_progress');
    const firstPending = sortedStages.findIndex(s => s.status === 'pending');
    const activeIdx = currentIdx >= 0 ? currentIdx : firstPending >= 0 ? firstPending : sortedStages.length;

    return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="job-detail-page">
      <Button variant="ghost" onClick={() => { setSelectedProject(null); loadBoard(); loadProjects(); }}
        className="mb-3 text-[#6b8a9e] font-semibold" data-testid="back-to-board-btn">
        Back to Jobs Board
      </Button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-extrabold text-[#1e3a50]">{selectedProject.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <Badge className={selectedProject.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-600'}>{selectedProject.status}</Badge>
            {selectedProject.customer_city && <span className="text-sm font-medium text-[#6b8a9e]">{selectedProject.customer_city}</span>}
            {selectedProject.bid_total > 0 && <span className="text-sm font-mono font-bold text-[#2a6496]">${selectedProject.bid_total.toLocaleString()}</span>}
            <span className="text-sm font-semibold text-[#4a9ac5]">{selectedProject.progress_pct}% complete</span>
          </div>
        </div>
        {selectedProject.is_overdue && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-red-500" /><span className="text-sm font-bold text-red-700">Overdue</span>
          </div>
        )}
      </div>

      {/* ── HORIZONTAL STAGE TIMELINE ── */}
      <Card className="border-0 shadow-[0_1px_4px_rgba(0,0,0,0.05)] mb-6">
        <CardContent className="p-5">
          <div className="overflow-x-auto">
            <div className="flex items-center min-w-max gap-0">
              {sortedStages.map((stage, idx) => {
                const isComplete = stage.status === 'completed';
                const isActive = idx === activeIdx;
                const isLocked = !isComplete && idx > activeIdx;
                const tasksDone = (stage.tasks || []).filter(t => t.done).length;
                const tasksTotal = (stage.tasks || []).length;
                return (
                  <div key={idx} className="flex items-center">
                    <div
                      className={`flex flex-col items-center cursor-pointer transition-all ${isLocked ? 'opacity-40' : ''}`}
                      onClick={() => {
                        if (isLocked) { toast.info(`Complete "${sortedStages[activeIdx]?.name}" first`); return; }
                        setStageDialog({ projectId: selectedProject.id, stageIdx: idx, stage });
                      }}
                      data-testid={`timeline-stage-${idx}`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                        isComplete ? 'bg-emerald-500 text-white' :
                        isActive ? 'bg-[#4a9ac5] text-white ring-4 ring-[#4a9ac5]/20' :
                        'bg-gray-200 text-gray-500'
                      }`}>
                        {isComplete ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                      </div>
                      <p className={`text-[10px] mt-1.5 text-center w-20 leading-tight font-semibold ${
                        isActive ? 'text-[#2a6496]' : isComplete ? 'text-emerald-700' : 'text-gray-400'
                      }`}>{stage.name}</p>
                      {tasksTotal > 0 && (
                        <p className={`text-[9px] font-bold ${tasksDone === tasksTotal ? 'text-emerald-500' : 'text-gray-400'}`}>
                          {tasksDone}/{tasksTotal}
                        </p>
                      )}
                    </div>
                    {idx < sortedStages.length - 1 && (
                      <div className={`h-[2px] w-8 mx-0.5 ${isComplete ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-white rounded-lg p-1 shadow-sm border border-[#d4e6ef]/50 w-fit overflow-x-auto">
        {[
          {k:'stages',l:'Stages'},
          {k:'change_orders',l:`Change Orders (${changeOrders.length})`},
          {k:'job_costing',l:'Job Costing'},
          {k:'photos',l:`Photos (${Object.values(stagePhotos).flat().length})`},
          {k:'expenses',l:`Expenses (${expenses.length})`}
        ].map(t => (
          <button key={t.k} onClick={() => setDetailTab(t.k)}
            className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all whitespace-nowrap ${detailTab === t.k ? 'bg-[#4a9ac5] text-white shadow-sm' : 'text-[#6b8a9e] hover:bg-[#e4f0f6]'}`}
            data-testid={`detail-tab-${t.k}`}>{t.l}</button>
        ))}
      </div>

      {/* PHOTOS TAB */}
      {detailTab === 'photos' && (
        <div className="space-y-6">
          {sortedStages.map((stage, idx) => {
            const photos = stagePhotos[idx] || [];
            if (photos.length === 0) return null;
            return (
              <div key={idx}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    stage.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-[#4a9ac5]/15 text-[#2a6496]'
                  }`}>{idx + 1}</div>
                  <h3 className="text-sm font-bold text-[#1e3a50]">{stage.name}</h3>
                  <Badge className="bg-[#e4f0f6] text-[#4a9ac5] text-[10px] font-bold">{photos.length} photos</Badge>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                  {photos.map(photo => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${selectedProject.id}/photos/${photo.id}`}
                        alt={photo.filename}
                        className="w-full aspect-square object-cover rounded-xl border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${selectedProject.id}/photos/${photo.id}`, '_blank')}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-xl p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-[9px] font-bold text-white truncate">{photo.stage_name || stage.name}</p>
                        <p className="text-[8px] text-white/70">{new Date(photo.uploaded_at).toLocaleDateString()}</p>
                      </div>
                      <button className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeletePhoto(selectedProject.id, photo.id)}>
                        <Trash2 className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {Object.values(stagePhotos).flat().length === 0 && (
            <div className="text-center py-12">
              <Camera className="w-12 h-12 text-gray-200 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-400">No photos uploaded yet</p>
              <p className="text-xs text-gray-300">Upload photos from the Stages tab</p>
            </div>
          )}
        </div>
      )}

      {/* STAGES TAB */}
      {detailTab === 'stages' && (
      <div className="space-y-3">
        {sortedStages.map((stage, idx) => {
          const sc = STATUS_COLORS[stage.status] || STATUS_COLORS.pending;
          const isOverdue = stage.status !== 'completed' && stage.due_date && new Date(stage.due_date + 'T23:59:59') < new Date();
          const isLocked = stage.status === 'pending' && idx > activeIdx;
          const tasks = stage.tasks || [];
          const tasksDone = tasks.filter(t => t.done).length;
          const photoCats = stage.photo_categories || [];
          const photosNeeded = stage.required_photos || 0;
          const photosHave = (stagePhotos[idx] || []).length;
          const canComplete = tasksDone === tasks.length && photosHave >= photosNeeded && (stage.pm_approved || false);

          return (
            <Card key={idx} className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] transition-all ${isOverdue ? 'ring-1 ring-red-200' : ''} ${isLocked ? 'opacity-40' : ''}`}
              data-testid={`stage-row-${idx}`}>
              <CardContent className="p-4">
                {/* Stage Header */}
                <div className="flex items-center gap-3 mb-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    stage.status === 'completed' ? 'bg-emerald-100' : stage.status === 'in_progress' ? 'bg-[#4a9ac5]/15' : 'bg-gray-100'
                  }`}>
                    {stage.status === 'completed' ? <CheckCircle className="w-4 h-4 text-emerald-600" /> :
                     <span className="text-xs font-bold text-gray-500">{idx + 1}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-[#1e3a50]">{stage.name}</p>
                    {stage.assigned_name && <span className="text-[11px] font-medium text-[#6b8a9e]">{stage.assigned_name}</span>}
                    {isOverdue && <span className="text-[10px] font-bold text-red-500 ml-2">OVERDUE</span>}
                  </div>
                  <Select value={stage.status} onValueChange={async v => {
                    const r = await handleStageUpdate(selectedProject.id, idx, { status: v });
                    if (r?.blocked) { toast.error(`Cannot complete: ${r.reasons.join(', ')}`); }
                  }}>
                    <SelectTrigger className={`w-32 h-8 text-xs ${sc.bg} ${sc.text} border-0 font-bold`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Complete</SelectItem>
                      <SelectItem value="blocked">Blocked</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Inline Notes — always visible */}
                <div className="ml-11 mb-3">
                  <Textarea
                    value={stage.notes || ''}
                    onChange={e => {
                      const newStages = [...sortedStages];
                      newStages[idx] = { ...newStages[idx], notes: e.target.value };
                      setSelectedProject(p => ({ ...p, stages: newStages }));
                    }}
                    onBlur={e => handleStageUpdate(selectedProject.id, idx, { notes: e.target.value })}
                    placeholder="Add stage notes..."
                    className="text-xs h-auto min-h-[32px] resize-none bg-[#f5f9fb] border-[#d4e6ef]/50 focus:bg-white"
                    rows={1}
                    data-testid={`stage-notes-inline-${idx}`}
                  />
                </div>

                {/* Tasks */}
                {tasks.length > 0 && (
                  <div className="ml-11 mb-3">
                    <p className="text-[10px] font-bold text-[#6b8a9e] uppercase tracking-wider mb-1.5">Tasks ({tasksDone}/{tasks.length})</p>
                    <div className="space-y-1">
                      {tasks.map((task, tidx) => (
                        <div key={tidx} className="flex items-center gap-2 cursor-pointer" onClick={() => handleStageUpdate(selectedProject.id, idx, { toggle_task: tidx })}>
                          <Checkbox checked={task.done} className="pointer-events-none" />
                          <span className={`text-xs font-medium ${task.done ? 'text-gray-400 line-through' : 'text-[#2a4a60]'}`}>{task.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Controls Row */}
                <div className="ml-11 flex flex-wrap items-center gap-2 mb-2">
                  {photosNeeded > 0 && (
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold ${photosHave >= photosNeeded ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                      <Camera className="w-3 h-3" /> {photosHave}/{photosNeeded} photos
                    </div>
                  )}
                  {/* Photo upload with category */}
                  <Button size="sm" variant="outline" className="h-7 text-[10px] font-bold border-[#c8dce8] text-[#4a9ac5]"
                    onClick={() => { setPhotoUploadStage(idx); setPhotoCategory(''); }}
                    data-testid={`photo-upload-btn-${idx}`}>
                    <Camera className="w-3 h-3 mr-1" /> Upload Photo
                  </Button>
                  {/* Editable required photo count */}
                  <div className="flex items-center gap-1 text-[10px] font-semibold text-[#6b8a9e]">
                    Req:
                    <Input type="number" min="0" max="20" value={photosNeeded} className="w-12 h-6 text-[10px] text-center p-0 font-bold"
                      onChange={e => {
                        const newStages = [...sortedStages];
                        newStages[idx] = { ...newStages[idx], required_photos: parseInt(e.target.value) || 0 };
                        setSelectedProject(p => ({ ...p, stages: newStages }));
                        handleStageUpdate(selectedProject.id, idx, { required_photos: parseInt(e.target.value) || 0 });
                      }}
                      data-testid={`required-photos-${idx}`} />
                  </div>
                  <button className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold transition-colors ${stage.pm_approved ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-600'}`}
                    onClick={() => handleStageUpdate(selectedProject.id, idx, { pm_approved: !stage.pm_approved })}>
                    <CheckCircle className="w-3 h-3" /> {stage.pm_approved ? 'PM Approved' : 'PM Sign-off'}
                  </button>
                  <Input type="date" value={stage.due_date || ''}
                    onChange={e => handleStageUpdate(selectedProject.id, idx, { due_date: e.target.value })}
                    className={`w-32 h-7 text-[11px] font-semibold ${isOverdue ? 'border-red-300 text-red-700' : ''}`} />
                  <Select value={stage.assigned_name || '_none'}
                    onValueChange={v => { const m = teamMembers.find(m => m.name === v); handleStageUpdate(selectedProject.id, idx, { assigned_name: v === '_none' ? '' : v, assigned_to: m ? m.id : '' }); }}>
                    <SelectTrigger className="w-36 h-7 text-[11px] font-semibold"><SelectValue placeholder="Assign..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Unassigned</SelectItem>
                      {teamMembers.map(m => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Photo categories checklist */}
                {photoCats.length > 0 && stage.status !== 'completed' && (
                  <div className="ml-11 mb-2 p-2.5 bg-[#f5f9fb] rounded-lg">
                    <p className="text-[9px] font-bold text-[#6b8a9e] uppercase tracking-wider mb-1">Photo Checklist</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                      {photoCats.map((cat, ci) => (
                        <span key={ci} className="text-[10px] font-medium text-[#4a6a7e] flex items-center gap-1">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#b0c8d8] flex-shrink-0" /> {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Photo thumbnails */}
                {(stagePhotos[idx] || []).length > 0 && (
                  <div className="flex gap-2 ml-11 mt-1 overflow-x-auto pb-1">
                    {stagePhotos[idx].map(photo => (
                      <div key={photo.id} className="relative group flex-shrink-0">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${selectedProject.id}/photos/${photo.id}`}
                          alt={photo.filename} className="w-14 h-14 object-cover rounded-lg border border-gray-200" />
                        <button className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => handleDeletePhoto(selectedProject.id, photo.id)}>
                          <Trash2 className="w-2.5 h-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Blocker message */}
                {stage.status !== 'completed' && !isLocked && !canComplete && (
                  <p className="ml-11 mt-1.5 text-[10px] font-semibold text-amber-600">
                    {tasksDone < tasks.length ? `${tasks.length - tasksDone} task(s) remaining` : ''}
                    {photosHave < photosNeeded ? ` | ${photosNeeded - photosHave} photo(s) needed` : ''}
                    {!stage.pm_approved ? ' | Awaiting PM sign-off' : ''}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      {/* EXPENSES TAB */}
      {detailTab === 'expenses' && (
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-bold text-[#2a4a60]">{expenses.length} expenses | Total: ${expenses.reduce((s, e) => s + (e.amount || 0), 0).toLocaleString()}</p>
          <Button size="sm" className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white"
            onClick={() => { setEditExpense(null); setExpForm({ vendor_name: '', amount: 0, category: '', date: new Date().toISOString().split('T')[0], notes: '' }); setExpenseDialog(true); }}
            data-testid="add-expense-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Expense
          </Button>
        </div>
        <div className="space-y-2">
          {expenses.map(exp => (
            <Card key={exp.id} className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid={`expense-${exp.id}`}>
              <CardContent className="p-3 flex items-center gap-4">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-[#1e3a50]">{exp.vendor_name}</p>
                  <p className="text-[11px] font-medium text-[#6b8a9e]">{exp.category} | {exp.date}</p>
                  {exp.notes && <p className="text-[10px] text-gray-400 truncate">{exp.notes}</p>}
                </div>
                <span className="text-base font-bold font-mono text-[#1e3a50]">${(exp.amount || 0).toLocaleString()}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => { setEditExpense(exp); setExpForm({ vendor_name: exp.vendor_name, amount: exp.amount, category: exp.category, date: exp.date, notes: exp.notes || '' }); setExpenseDialog(true); }}>
                    <Pencil className="w-3 h-3 text-gray-400" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteExpense(exp.id)}>
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {expenses.length === 0 && <p className="text-center py-8 text-sm font-medium text-gray-400">No expenses recorded yet</p>}
        </div>
      </div>
      )}

      {/* CHANGE ORDERS TAB */}
      {detailTab === 'change_orders' && (
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-bold text-[#2a4a60]">{changeOrders.length} change order{changeOrders.length !== 1 ? 's' : ''}</p>
          <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={() => { setCoForm({ title: '', reason_code: 'scope_change', scope_description: '', requested_cost_impact: 0, requested_price_impact: 0, days_added: 0, schedule_impacted: false, work_started_before_approval: false, notes: '' }); setCoDialog(true); }}
            data-testid="add-co-btn"><Plus className="w-3.5 h-3.5 mr-1" /> New Change Order</Button>
        </div>
        <div className="space-y-3">
          {changeOrders.map(co => {
            const statusColors = { draft:'bg-gray-100 text-gray-600', submitted:'bg-blue-50 text-blue-700', pricing_review:'bg-violet-50 text-violet-700', pending_customer:'bg-amber-50 text-amber-700', approved:'bg-emerald-50 text-emerald-700', rejected:'bg-red-50 text-red-600', void:'bg-gray-200 text-gray-500' };
            return (
            <Card key={co.id} className={`border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)] ${co.work_started_before_approval ? 'ring-1 ring-amber-200' : ''}`} data-testid={`co-${co.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-extrabold text-[#2a6496] font-mono">{co.co_number}</span>
                      <p className="text-sm font-bold text-[#1e3a50]">{co.title}</p>
                      {co.work_started_before_approval && <Badge className="bg-amber-100 text-amber-700 text-[8px]">WORK STARTED</Badge>}
                      {co.schedule_impacted && <Badge className="bg-orange-100 text-orange-700 text-[8px]">SCHEDULE IMPACTED</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge className="bg-gray-100 text-gray-600 text-[9px] capitalize">{(co.reason_code || '').replace(/_/g, ' ')}</Badge>
                      {co.days_added > 0 && <span className="text-[10px] font-bold text-amber-600">+{co.days_added} days</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[10px] font-bold capitalize ${statusColors[co.status] || statusColors.draft}`}>{(co.status || 'draft').replace(/_/g, ' ')}</Badge>
                    {co.customer_signed && <Badge className="bg-blue-50 text-blue-700 text-[9px]">Signed</Badge>}
                  </div>
                </div>
                {co.scope_description && <p className="text-xs font-medium text-[#4a6a7e] mb-3">{co.scope_description}</p>}
                {/* Requested vs Approved */}
                <div className="grid grid-cols-2 gap-3 mb-2">
                  <div className="p-2 bg-[#f5f9fb] rounded-lg">
                    <p className="text-[9px] font-bold text-[#6b8a9e] uppercase mb-1">Requested</p>
                    <div className="flex gap-4 text-xs">
                      <span className="font-bold">Cost: <span className="font-mono text-red-600">${(co.requested_cost_impact || co.cost_impact || 0).toLocaleString()}</span></span>
                      <span className="font-bold">Price: <span className="font-mono text-emerald-700">+${(co.requested_price_impact || co.price_impact || 0).toLocaleString()}</span></span>
                    </div>
                  </div>
                  {co.status === 'approved' && (
                    <div className="p-2 bg-emerald-50 rounded-lg">
                      <p className="text-[9px] font-bold text-emerald-600 uppercase mb-1">Approved</p>
                      <div className="flex gap-4 text-xs">
                        <span className="font-bold">Cost: <span className="font-mono text-red-600">${(co.approved_cost_impact || 0).toLocaleString()}</span></span>
                        <span className="font-bold">Price: <span className="font-mono text-emerald-700">+${(co.approved_price_impact || 0).toLocaleString()}</span></span>
                      </div>
                    </div>
                  )}
                </div>
                {/* Status actions */}
                {co.status === 'draft' && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" className="h-7 text-[10px] bg-blue-500 hover:bg-blue-600 text-white" onClick={async () => {
                      await api.put(`/projects/${selectedProject.id}/change-orders/${co.id}`, { status: 'submitted' });
                      toast.success('Submitted for review'); openProjectDetail(selectedProject.id);
                    }}>Submit</Button>
                  </div>
                )}
                {['submitted', 'pricing_review', 'pending_customer'].includes(co.status) && (
                  <div className="flex gap-2 mt-2">
                    {co.status === 'submitted' && (
                      <Button size="sm" className="h-7 text-[10px] bg-violet-500 hover:bg-violet-600 text-white" onClick={async () => {
                        await api.put(`/projects/${selectedProject.id}/change-orders/${co.id}`, { status: 'pricing_review' });
                        toast.success('Moved to pricing review'); openProjectDetail(selectedProject.id);
                      }}>Start Pricing</Button>
                    )}
                    {co.status === 'pricing_review' && (
                      <Button size="sm" className="h-7 text-[10px] bg-amber-500 hover:bg-amber-600 text-white" onClick={async () => {
                        await api.put(`/projects/${selectedProject.id}/change-orders/${co.id}`, { status: 'pending_customer' });
                        toast.success('Sent to customer'); openProjectDetail(selectedProject.id);
                      }}>Send to Customer</Button>
                    )}
                    <Button size="sm" className="h-7 text-[10px] bg-emerald-500 hover:bg-emerald-600 text-white" onClick={async () => {
                      await api.put(`/projects/${selectedProject.id}/change-orders/${co.id}`, { status: 'approved', approved_cost_impact: co.requested_cost_impact || co.cost_impact, approved_price_impact: co.requested_price_impact || co.price_impact });
                      toast.success('Approved'); openProjectDetail(selectedProject.id);
                    }}>Approve</Button>
                    <Button size="sm" variant="outline" className="h-7 text-[10px] text-red-500 border-red-200" onClick={async () => {
                      await api.put(`/projects/${selectedProject.id}/change-orders/${co.id}`, { status: 'rejected' });
                      toast.success('Rejected'); openProjectDetail(selectedProject.id);
                    }}>Reject</Button>
                    {!co.customer_signed && co.status === 'pending_customer' && (
                      <Button size="sm" variant="outline" className="h-7 text-[10px] text-[#4a9ac5]" onClick={async () => {
                        await api.put(`/projects/${selectedProject.id}/change-orders/${co.id}`, { customer_signed: true });
                        toast.success('Customer signed'); openProjectDetail(selectedProject.id);
                      }}>Mark Signed</Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            );
          })}
          {changeOrders.length === 0 && <p className="text-center py-8 text-sm font-medium text-gray-400">No change orders</p>}
        </div>
      </div>
      )}

      {/* JOB COSTING TAB */}
      {detailTab === 'job_costing' && jobCosting && (
      <div className="space-y-4">
        {/* Contract Summary */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#1e3a50] mb-4">Contract Summary</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div><p className="text-[10px] font-bold text-[#6b8a9e] uppercase">Original Contract</p><p className="text-lg font-extrabold font-mono text-[#1e3a50]">${jobCosting.original_contract.toLocaleString()}</p></div>
              <div><p className="text-[10px] font-bold text-emerald-600 uppercase">Approved COs ({jobCosting.approved_co_count})</p><p className="text-lg font-extrabold font-mono text-emerald-700">+${jobCosting.approved_change_orders.toLocaleString()}</p></div>
              {jobCosting.pending_co_count > 0 && (
                <div><p className="text-[10px] font-bold text-amber-600 uppercase">Pending COs ({jobCosting.pending_co_count})</p><p className="text-lg font-extrabold font-mono text-amber-700">+${jobCosting.pending_change_orders.toLocaleString()}</p><p className="text-[8px] font-bold text-amber-500">not yet in revised</p></div>
              )}
              <div className="bg-[#f5f9fb] rounded-lg p-2.5"><p className="text-[10px] font-bold text-[#2a6496] uppercase">Revised Contract</p><p className="text-xl font-extrabold font-mono text-[#1e3a50]">${jobCosting.revised_contract.toLocaleString()}</p></div>
              {jobCosting.co_days_added > 0 && <div><p className="text-[10px] font-bold text-amber-600 uppercase">Schedule Impact</p><p className="text-lg font-extrabold font-mono text-amber-700">+{jobCosting.co_days_added} days</p></div>}
            </div>
          </CardContent>
        </Card>

        {/* Cost Tracking */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#1e3a50] mb-4">Cost Tracking</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div><p className="text-[10px] font-bold text-[#6b8a9e] uppercase">Est. Cost at Sale</p><p className="text-lg font-extrabold font-mono text-[#1e3a50]">${(jobCosting.estimated_cost_at_sale || 0).toLocaleString()}</p></div>
              <div><p className="text-[10px] font-bold text-[#6b8a9e] uppercase">Current Forecast</p><p className="text-lg font-extrabold font-mono text-[#2a6496]">${(jobCosting.current_forecasted_cost || 0).toLocaleString()}</p></div>
              <div><p className="text-[10px] font-bold text-red-500 uppercase">Actual to Date</p><p className="text-lg font-extrabold font-mono text-red-600">${jobCosting.actual_cost.toLocaleString()}</p></div>
              <div><p className="text-[10px] font-bold text-violet-600 uppercase">Committed Cost</p><p className="text-lg font-extrabold font-mono text-violet-700">${(jobCosting.committed_cost || 0).toLocaleString()}</p></div>
              <div><p className="text-[10px] font-bold text-[#6b8a9e] uppercase">Cost Remaining</p><p className="text-lg font-extrabold font-mono text-[#4a6a7e]">${jobCosting.cost_remaining.toLocaleString()}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Profit Analysis */}
        <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-[#1e3a50] mb-4">Profit Analysis</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <div><p className="text-[10px] font-bold text-[#6b8a9e] uppercase">Original GP</p><p className="text-lg font-extrabold font-mono text-[#1e3a50]">${jobCosting.original_gross_profit.toLocaleString()}</p></div>
              <div className="bg-emerald-50 rounded-lg p-2.5"><p className="text-[10px] font-bold text-emerald-600 uppercase">Forecasted GP</p><p className="text-lg font-extrabold font-mono text-emerald-700">${jobCosting.forecasted_gross_profit.toLocaleString()}</p><p className="text-[9px] font-bold text-emerald-500">{jobCosting.forecasted_margin_pct}% margin</p></div>
              <div><p className="text-[10px] font-bold text-[#6b8a9e] uppercase">Actual GP</p><p className="text-lg font-extrabold font-mono text-[#1e3a50]">${jobCosting.actual_gross_profit.toLocaleString()}</p><p className="text-[9px] font-bold text-[#6b8a9e]">{jobCosting.actual_margin_pct}% margin</p></div>
              <div className={`rounded-lg p-2.5 ${jobCosting.profit_fade_gain >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}><p className={`text-[10px] font-bold uppercase ${jobCosting.profit_fade_gain >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Profit {jobCosting.profit_fade_gain >= 0 ? 'Gain' : 'Fade'}</p><p className={`text-lg font-extrabold font-mono ${jobCosting.profit_fade_gain >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{jobCosting.profit_fade_gain >= 0 ? '+' : ''}${jobCosting.profit_fade_gain.toLocaleString()}</p></div>
            </div>
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        {jobCosting.breakdown.length > 0 && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-[#1e3a50] mb-3">Category Breakdown</h3>
              <div className="space-y-1.5">
                {jobCosting.breakdown.map(cat => (
                  <div key={cat.category} className="flex items-center justify-between py-1.5 px-3 bg-[#f5f9fb] rounded-lg">
                    <span className="text-xs font-bold text-[#2a4a60] capitalize w-28">{cat.category}</span>
                    <div className="flex gap-6 text-xs font-mono">
                      <span className="text-[#6b8a9e] w-24 text-right">Plan: ${cat.planned.toLocaleString()}</span>
                      <span className="text-[#1e3a50] w-24 text-right font-bold">Act: ${cat.actual.toLocaleString()}</span>
                      <span className={`w-24 text-right font-bold ${cat.variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{cat.variance >= 0 ? '+' : ''}${cat.variance.toLocaleString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* CO Impact Detail */}
        {jobCosting.change_orders_summary.length > 0 && (
          <Card className="border-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
            <CardContent className="p-5">
              <h3 className="text-sm font-bold text-[#1e3a50] mb-3">Change Order Detail</h3>
              <div className="space-y-1.5">
                {jobCosting.change_orders_summary.map(co => {
                  const sc = { approved:'bg-emerald-50 text-emerald-700', rejected:'bg-red-50 text-red-600', void:'bg-gray-100 text-gray-500' };
                  return (
                  <div key={co.co_number} className="flex items-center justify-between py-2 px-3 bg-[#f5f9fb] rounded-lg">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="text-xs font-extrabold text-[#2a6496] font-mono">{co.co_number}</span>
                      <span className="text-xs font-bold text-[#2a4a60] truncate">{co.title}</span>
                      {co.work_started && <Badge className="bg-amber-100 text-amber-700 text-[7px]">WIP</Badge>}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p className="text-[9px] font-bold text-[#6b8a9e]">Req: ${(co.requested_price || 0).toLocaleString()}</p>
                        {co.approved_price > 0 && <p className="text-[9px] font-bold text-emerald-600">Appr: ${co.approved_price.toLocaleString()}</p>}
                      </div>
                      {co.days > 0 && <span className="text-[9px] font-bold text-amber-600">+{co.days}d</span>}
                      <Badge className={`text-[8px] capitalize ${sc[co.status] || 'bg-amber-50 text-amber-700'}`}>{(co.status || '').replace(/_/g, ' ')}</Badge>
                    </div>
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      )}

      {/* Photo Upload with Category Dialog */}
      <Dialog open={photoUploadStage !== null} onOpenChange={() => setPhotoUploadStage(null)}>
        <DialogContent className="max-w-sm" data-testid="photo-upload-dialog">
          <DialogHeader><DialogTitle>Upload Photo — {sortedStages[photoUploadStage]?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(sortedStages[photoUploadStage]?.photo_categories || []).length > 0 && (
              <div>
                <Label className="text-xs font-bold">Photo Category</Label>
                <Select value={photoCategory} onValueChange={setPhotoCategory}>
                  <SelectTrigger className="mt-1" data-testid="photo-category-select"><SelectValue placeholder="Select what this photo is..." /></SelectTrigger>
                  <SelectContent>
                    {(sortedStages[photoUploadStage]?.photo_categories || []).map((cat, i) => (
                      <SelectItem key={i} value={cat}>{cat}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold">Select Photo</Label>
              <Input type="file" accept="image/*" className="mt-1"
                onChange={e => {
                  if (e.target.files[0] && photoUploadStage !== null) {
                    handlePhotoUpload(selectedProject.id, photoUploadStage, e.target.files[0], photoCategory);
                    setPhotoUploadStage(null);
                  }
                }} data-testid="photo-file-input" />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={expenseDialog} onOpenChange={setExpenseDialog}>
        <DialogContent className="max-w-md" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>{editExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Vendor / Payee</Label><Input value={expForm.vendor_name} onChange={e => setExpForm(p => ({...p, vendor_name: e.target.value}))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-bold">Amount ($)</Label><Input type="number" value={expForm.amount} onChange={e => setExpForm(p => ({...p, amount: parseFloat(e.target.value) || 0}))} /></div>
              <div><Label className="text-xs font-bold">Category</Label><Input value={expForm.category} onChange={e => setExpForm(p => ({...p, category: e.target.value}))} placeholder="excavation, plumbing..." /></div>
            </div>
            <div><Label className="text-xs font-bold">Date</Label><Input type="date" value={expForm.date} onChange={e => setExpForm(p => ({...p, date: e.target.value}))} /></div>
            <div><Label className="text-xs font-bold">Notes</Label><Textarea value={expForm.notes} onChange={e => setExpForm(p => ({...p, notes: e.target.value}))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialog(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={handleSaveExpense}>{editExpense ? 'Update' : 'Add'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Order Dialog */}
      <Dialog open={coDialog} onOpenChange={setCoDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="co-dialog">
          <DialogHeader><DialogTitle>New Change Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-bold">Title *</Label><Input value={coForm.title} onChange={e => setCoForm(p => ({...p, title: e.target.value}))} placeholder="Add sheer descent waterfall..." data-testid="co-title" /></div>
            <div>
              <Label className="text-xs font-bold">Reason Code</Label>
              <Select value={coForm.reason_code} onValueChange={v => setCoForm(p => ({...p, reason_code: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['scope_change','site_conditions','customer_request','design_change','unforeseen','material_change','code_requirement','weather','other'].map(r => (
                    <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs font-bold">Scope Description</Label><Textarea value={coForm.scope_description} onChange={e => setCoForm(p => ({...p, scope_description: e.target.value}))} rows={3} placeholder="Detailed scope of the change..." /></div>
            <div className="p-3 bg-[#f5f9fb] rounded-lg border border-[#d4e6ef]/50">
              <p className="text-[10px] font-bold text-[#2a6496] uppercase mb-2">Financial Impact</p>
              <div className="grid grid-cols-3 gap-3">
                <div><Label className="text-[10px] font-bold text-red-600">Cost Impact ($)</Label><Input type="number" value={coForm.requested_cost_impact || coForm.cost_impact || 0} onChange={e => setCoForm(p => ({...p, requested_cost_impact: parseFloat(e.target.value) || 0}))} data-testid="co-cost" /></div>
                <div><Label className="text-[10px] font-bold text-emerald-600">Price Impact ($)</Label><Input type="number" value={coForm.requested_price_impact || coForm.price_impact || 0} onChange={e => setCoForm(p => ({...p, requested_price_impact: parseFloat(e.target.value) || 0}))} data-testid="co-price" /></div>
                <div><Label className="text-[10px] font-bold text-amber-600">Days Added</Label><Input type="number" value={coForm.days_added} onChange={e => setCoForm(p => ({...p, days_added: parseInt(e.target.value) || 0}))} /></div>
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Checkbox checked={coForm.schedule_impacted || false} onCheckedChange={v => setCoForm(p => ({...p, schedule_impacted: v}))} />
                <Label className="text-xs font-bold">Schedule Impacted</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox checked={coForm.work_started_before_approval || false} onCheckedChange={v => setCoForm(p => ({...p, work_started_before_approval: v}))} />
                <Label className="text-xs font-bold text-amber-700">Work Already Started</Label>
              </div>
            </div>
            <div><Label className="text-xs font-bold">Notes</Label><Input value={coForm.notes} onChange={e => setCoForm(p => ({...p, notes: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCoDialog(false)}>Cancel</Button>
            <Button className="bg-[#4a9ac5] hover:bg-[#3d87b3] text-white" onClick={async () => {
              await api.post(`/projects/${selectedProject.id}/change-orders`, coForm);
              toast.success('Change order created'); setCoDialog(false); openProjectDetail(selectedProject.id);
            }} disabled={!coForm.title?.trim()} data-testid="co-save-btn">Create CO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );}

  // ─── MAIN BOARD VIEW ───
  return (
    <TooltipProvider>
      <div className={`p-4 sm:p-6 lg:p-8 ${forceDesktop ? 'min-w-[1200px]' : ''}`} data-testid="jobs-board-page">
        {/* Header */}
        <div className="flex flex-col gap-3 mb-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-slate-900 tracking-tight">Jobs Board</h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {projects.filter(p => p.status === 'active').length} active
                {projects.some(p => p.is_overdue) && (
                  <span className="text-red-500 ml-2 font-semibold">
                    {projects.filter(p => p.is_overdue).length} overdue
                  </span>
                )}
              </p>
            </div>
            {/* Desktop/Mobile toggle */}
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs border-slate-200"
              onClick={() => setForceDesktop(!forceDesktop)}
              data-testid="toggle-desktop-mobile-btn"
            >
              {forceDesktop ? <Smartphone className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{forceDesktop ? 'Mobile View' : 'Desktop View'}</span>
            </Button>
          </div>
          {/* Toolbar */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[140px] max-w-xs">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Search jobs..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-8 h-9 text-sm" data-testid="jobs-search" />
            </div>
            <Select value={filterAssignee} onValueChange={setFilterAssignee}>
              <SelectTrigger className="w-36 sm:w-40 h-9 text-sm" data-testid="jobs-filter-assignee">
                <Filter className="w-3.5 h-3.5 mr-1" /><SelectValue placeholder="All Team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team</SelectItem>
                {allAssignees.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              <Button variant={view === 'kanban' ? 'default' : 'ghost'} size="sm"
                className={`h-8 px-3 ${view === 'kanban' ? 'bg-white shadow-sm' : ''}`}
                onClick={() => setView('kanban')} data-testid="view-kanban-btn">
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button variant={view === 'table' ? 'default' : 'ghost'} size="sm"
                className={`h-8 px-3 ${view === 'table' ? 'bg-white shadow-sm' : ''}`}
                onClick={() => setView('table')} data-testid="view-table-btn">
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* ─── KANBAN VIEW ─── */}
        {view === 'kanban' && (
          <>
            {/* Desktop kanban (hidden on small screens unless force desktop) */}
            <div className={`overflow-x-auto pb-4 ${forceDesktop ? 'block' : 'hidden md:block'}`}>
              <div className="flex gap-4 min-w-max">
                {stages.map(stageName => {
                  const stageProjects = (board[stageName] || []).filter(p =>
                    (!search || p.name.toLowerCase().includes(search.toLowerCase())) &&
                    (filterAssignee === 'all' || p.current_assignee === filterAssignee ||
                     p.stages?.some(s => s.assigned_name === filterAssignee))
                  );
                  const isCompleted = stageName === 'Completed';
                  return (
                    <div key={stageName} className="w-[260px] flex-shrink-0">
                      <div className="flex items-center gap-2 mb-3 px-1">
                        <div className={`w-2.5 h-2.5 rounded-full ${isCompleted ? 'bg-emerald-500' : 'bg-sky-400'}`} />
                        <h3 className="text-xs font-bold text-slate-600 uppercase tracking-wider">{stageName}</h3>
                        <Badge variant="secondary" className="ml-auto text-[10px] font-mono h-5 px-1.5">{stageProjects.length}</Badge>
                      </div>
                      <div className={`space-y-2 rounded-xl p-2 min-h-[200px] ${isCompleted ? 'bg-emerald-50/40' : 'bg-slate-50/60'}`}>
                        {stageProjects.map(project => (
                          <ProjectCard key={project.id} project={project} onClick={() => openProjectDetail(project.id)} />
                        ))}
                        {stageProjects.length === 0 && (
                          <div className="flex items-center justify-center h-20 text-slate-300">
                            <CircleDot className="w-5 h-5" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Mobile card list (shown on small screens) */}
            <div className={`space-y-3 ${forceDesktop ? 'hidden' : 'md:hidden'}`}>
              {filteredProjects.length === 0 && (
                <div className="text-center py-12 text-slate-400">No jobs found</div>
              )}
              {filteredProjects.map(project => (
                <MobileJobCard key={project.id} project={project} onClick={() => openProjectDetail(project.id)} />
              ))}
            </div>
          </>
        )}

        {/* ─── TABLE VIEW ─── */}
        {view === 'table' && (
          <>
            {/* Desktop table (hidden on small screens unless force desktop) */}
            <Card className={`border-slate-100 ${forceDesktop ? 'block' : 'hidden md:block'}`}>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-bold text-slate-700 text-xs">Project</TableHead>
                    <TableHead className="font-bold text-slate-700 text-xs">City</TableHead>
                    <TableHead className="font-bold text-slate-700 text-xs">Current Stage</TableHead>
                    <TableHead className="font-bold text-slate-700 text-xs">Assignee</TableHead>
                    <TableHead className="font-bold text-slate-700 text-xs text-center">Progress</TableHead>
                    <TableHead className="font-bold text-slate-700 text-xs text-center">Days</TableHead>
                    <TableHead className="font-bold text-slate-700 text-xs text-right">Value</TableHead>
                    <TableHead className="font-bold text-slate-700 text-xs text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjects.map(project => {
                    const isOverdue = project.is_overdue;
                    return (
                      <TableRow key={project.id}
                        className={`cursor-pointer transition-colors ${isOverdue ? 'bg-red-50/50 hover:bg-red-50' : 'hover:bg-sky-50/50'}`}
                        onClick={() => openProjectDetail(project.id)}
                        data-testid={`job-row-${project.id}`}
                      >
                        <TableCell>
                          <p className="text-sm font-semibold text-slate-800">{project.name}</p>
                          <p className="text-[10px] text-slate-400 capitalize">{project.pool_type}</p>
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{project.customer_city}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] font-semibold ${
                            project.current_stage === 'Completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'
                          }`}>{project.current_stage}</Badge>
                        </TableCell>
                        <TableCell>
                          {project.current_assignee ? (
                            <div className="flex items-center gap-1.5">
                              <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center">
                                <span className="text-[9px] font-bold text-sky-700">{project.current_assignee.charAt(0)}</span>
                              </div>
                              <span className="text-xs text-slate-600">{project.current_assignee}</span>
                            </div>
                          ) : <span className="text-xs text-slate-300">—</span>}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2">
                            <Progress value={project.progress_pct || 0} className="h-1.5 w-16" />
                            <span className="text-[10px] font-mono text-slate-500">{project.progress_pct}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <DaysChip days={project.days_in_stage} isOverdue={isOverdue} />
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          ${(project.bid_total || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center">
                          {isOverdue ? (
                            <AlertTriangle className="w-4 h-4 text-red-500 mx-auto" />
                          ) : project.current_stage === 'Completed' ? (
                            <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                          ) : (
                            <Clock className="w-4 h-4 text-sky-400 mx-auto" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {filteredProjects.length === 0 && (
                <div className="text-center py-12 text-slate-400">No jobs found</div>
              )}
            </Card>
            {/* Mobile card list for table view too */}
            <div className={`space-y-3 ${forceDesktop ? 'hidden' : 'md:hidden'}`}>
              {filteredProjects.length === 0 && (
                <div className="text-center py-12 text-slate-400">No jobs found</div>
              )}
              {filteredProjects.map(project => (
                <MobileJobCard key={project.id} project={project} onClick={() => openProjectDetail(project.id)} />
              ))}
            </div>
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
